import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Lock, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { PHASES, getPhaseForSection } from '@/constants/phases';
import { dossiersApi } from '@/api';

/**
 * Sidebar accordéon affichant les 5 phases du workflow avec leur progression.
 * Remplace TabNavigation dans ExpertiseFormPage.
 */
export default function PhaseSidebar({
  dossier,
  expertise,
  currentSection,
  onNavigate,
  onValidatePhase,
}) {
  const navigate = useNavigate();
  const [expandedPhase, setExpandedPhase] = useState(dossier?.phase || 1);
  const [phaseProgress, setPhaseProgress] = useState(null);

  // Fetch progress on mount and when dossier changes
  useEffect(() => {
    if (!dossier?.id) return;
    dossiersApi
      .getPhaseProgress(dossier.id)
      .then((res) => setPhaseProgress(res.phases))
      .catch(() => {});
  }, [dossier?.id, dossier?.phase, dossier?.phases_validees]);

  // Auto-expand the phase containing the current section
  useEffect(() => {
    if (!currentSection) return;
    const phase = getPhaseForSection(currentSection);
    if (phase) setExpandedPhase(phase.id);
  }, [currentSection]);

  const phasesValidees = dossier?.phases_validees || [];
  const currentPhase = dossier?.phase || 1;

  const isPhaseValidated = (phaseId) => phasesValidees.includes(phaseId);
  const isPhaseAccessible = (phaseId) => {
    if (phaseId === 1) return true;
    return isPhaseValidated(phaseId - 1);
  };
  const isPhaseActive = (phaseId) => phaseId === currentPhase;

  // Get section completion status from progress API
  const getSectionStatus = (phaseId, sectionId) => {
    if (!phaseProgress) return 'pending';
    const phase = phaseProgress.find((p) => p.phase === phaseId);
    if (!phase) return 'pending';
    const section = phase.sections.find((s) => s.id === sectionId);
    if (!section) return 'pending';
    if (section.complete) return 'complete';
    if (sectionId === currentSection) return 'active';
    return 'pending';
  };

  // Count completed required sections for validate button
  const getPhaseCompletion = (phaseId) => {
    if (!phaseProgress) return { done: 0, total: 0 };
    const phase = phaseProgress.find((p) => p.phase === phaseId);
    if (!phase) return { done: 0, total: 0 };
    return { done: phase.sections_complete, total: phase.sections_total };
  };

  const canValidate = (phaseId) => {
    const { done, total } = getPhaseCompletion(phaseId);
    return done === total && total > 0 && !isPhaseValidated(phaseId);
  };

  const handlePhaseClick = (phaseId) => {
    if (!isPhaseAccessible(phaseId)) return;
    setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
  };

  const handleSectionClick = (sectionId) => {
    onNavigate(sectionId);
  };

  // Filter sections based on conditions (BCG, requiresBien)
  const getVisibleSections = (phase) => {
    return phase.sections.filter((section) => {
      if (section.conditional === 'bcg') {
        return expertise?.format_rapport === 'bcg';
      }
      return true;
    });
  };

  const adresse = expertise?.parcelles?.[0]?.adresse;

  return (
    <div className="w-[230px] flex-shrink-0 flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Dossier info */}
      <div className="p-3 bg-surface-muted border-b border-slate-100">
        <div className="text-[10px] text-slate-400 font-medium">Dossier</div>
        <div className="text-sm font-bold text-slate-900 truncate">
          {dossier?.reference || '—'}
        </div>
        {adresse && (
          <div className="text-[10px] text-slate-500 mt-0.5 truncate">
            {adresse}
          </div>
        )}
      </div>

      {/* Phases label */}
      <div className="px-3 pt-3 pb-1">
        <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">
          Phases du dossier
        </span>
      </div>

      {/* Phase list */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
        {PHASES.map((phase) => {
          const validated = isPhaseValidated(phase.id);
          const accessible = isPhaseAccessible(phase.id);
          const active = isPhaseActive(phase.id);
          const expanded = expandedPhase === phase.id;
          const visibleSections = getVisibleSections(phase);

          // Locked phase
          if (!accessible) {
            return (
              <div
                key={phase.id}
                className="flex items-center gap-2 px-2.5 py-2 bg-surface-muted rounded-lg opacity-45"
              >
                <div className="w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lock className="w-2.5 h-2.5 text-slate-400" />
                </div>
                <div>
                  <div className="text-[11px] font-medium text-slate-400">
                    {phase.id}. {phase.shortLabel}
                  </div>
                  <div className="text-[9px] text-slate-400">
                    Phase {phase.id - 1} requise
                  </div>
                </div>
              </div>
            );
          }

          // Accessible phase (validated or active)
          const bgClass = validated && !expanded
            ? 'bg-carex-50'
            : active
              ? 'bg-orange-50 border border-orange-200'
              : 'bg-white border border-slate-100';

          const textClass = validated
            ? 'text-carex-600'
            : active
              ? 'text-orange-600'
              : 'text-slate-700';

          return (
            <div key={phase.id} className={`rounded-lg ${bgClass}`}>
              {/* Phase header */}
              <button
                type="button"
                onClick={() => handlePhaseClick(phase.id)}
                className="w-full flex items-center gap-2 px-2.5 py-2 cursor-pointer"
              >
                {/* Circle indicator */}
                {validated ? (
                  <div className="w-5 h-5 bg-carex-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                ) : (
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      active ? 'bg-orange-500' : 'bg-slate-300'
                    }`}
                  >
                    <span className="text-[10px] font-bold text-white">
                      {phase.id}
                    </span>
                  </div>
                )}

                <div className="flex-1 text-left">
                  <div className={`text-[11px] font-semibold ${textClass}`}>
                    {validated && !expanded
                      ? `${phase.id}. ${phase.shortLabel}`
                      : phase.shortLabel}
                  </div>
                  {validated && !expanded && (
                    <div className="text-[9px] text-slate-500">Validée</div>
                  )}
                  {active && !expanded && (
                    <div className="text-[9px] text-slate-500">
                      {getPhaseCompletion(phase.id).done}/
                      {getPhaseCompletion(phase.id).total} sections
                    </div>
                  )}
                </div>

                {expanded ? (
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                )}
              </button>

              {/* Expanded sub-sections */}
              {expanded && (
                <div className="px-2.5 pb-2.5 pt-0.5 pl-9 space-y-1">
                  {visibleSections.map((section) => {
                    const status = getSectionStatus(phase.id, section.id);
                    const isCurrent = section.id === currentSection;

                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => handleSectionClick(section.id)}
                        className={`w-full flex items-center gap-1.5 py-1 px-1.5 rounded text-left transition-colors ${
                          isCurrent
                            ? 'bg-white border border-orange-500 shadow-sm'
                            : 'hover:bg-white/60'
                        }`}
                      >
                        {/* Status dot */}
                        {status === 'complete' ? (
                          <div className="w-3.5 h-3.5 bg-carex-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-2 h-2 text-white" />
                          </div>
                        ) : isCurrent ? (
                          <div className="w-3.5 h-3.5 bg-orange-500 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 border-[1.5px] border-slate-300 rounded-full flex-shrink-0" />
                        )}

                        <span
                          className={`text-[10px] ${
                            isCurrent
                              ? 'text-orange-600 font-semibold'
                              : status === 'complete'
                                ? 'text-carex-600'
                                : 'text-slate-500'
                          }`}
                        >
                          {section.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom actions */}
      <div className="p-2 border-t border-slate-100 space-y-1.5">
        {/* Validate phase button */}
        <button
          type="button"
          onClick={() => onValidatePhase(currentPhase)}
          disabled={!canValidate(currentPhase)}
          className={`w-full py-2 rounded-lg text-[11px] font-semibold text-center transition-colors ${
            canValidate(currentPhase)
              ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          Valider la phase {currentPhase} (
          {getPhaseCompletion(currentPhase).done}/
          {getPhaseCompletion(currentPhase).total})
        </button>

        {/* Rapport link */}
        <button
          type="button"
          onClick={() => navigate(`/dossiers/${dossier?.id}`)}
          className="w-full flex items-center justify-center gap-1.5 py-2 bg-white border border-slate-200 rounded-lg text-[11px] font-medium text-slate-500 hover:bg-slate-50 cursor-pointer transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          Voir le rapport
        </button>
      </div>
    </div>
  );
}
